import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { adminAPI, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Users, Wallet, Download, Clock, CheckCircle2, XCircle, TrendingUp, Eye, Plus, Mail, Trash2 } from 'lucide-react';

const TABS = [
  { id: 'stats',     label: 'İstatistikler' },
  { id: 'requests',  label: 'Bakiye Talepleri' },
  { id: 'users',     label: 'Kullanıcılar' },
  { id: 'pricing',   label: 'Fiyatlandırma' },
  { id: 'settings',  label: 'Ayarlar' },
  { id: 'downloads', label: 'İndirmeler' },
  { id: 'contact',   label: 'İletişim' },
];

export default function AdminPanel() {
  const [tab, setTab] = useState('stats');
  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="mb-6 flex items-center gap-3">
          <h1 className="text-3xl font-bold">Admin Paneli</h1>
          <span className="text-xs px-2 py-1 rounded-full bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30">admin</span>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                tab===t.id ? 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30' : 'bg-white/5 text-muted-foreground hover:text-foreground border border-white/10'
              }`}>{t.label}</button>
          ))}
        </div>

        {tab === 'stats' && <StatsTab />}
        {tab === 'requests' && <RequestsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'pricing' && <PricingTab />}
        {tab === 'settings' && <SettingsTab />}
        {tab === 'downloads' && <DownloadsTab />}
        {tab === 'contact' && <ContactTab />}
      </div>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    adminAPI.stats()
      .then(r => { if (!cancelled) setStats(r.data); })
      .catch(e => { if (!cancelled) toast.error(formatApiError(e)); });
    return () => { cancelled = true; };
  }, []);
  if (!stats) return <div className="text-muted-foreground">Yükleniyor...</div>;

  const cards = [
    { icon: Users, label: 'Toplam Kullanıcı', value: stats.total_users, color: 'text-indigo-400' },
    { icon: Download, label: 'Toplam İndirme', value: stats.total_downloads, color: 'text-fuchsia-400' },
    { icon: Clock, label: 'Bekleyen Talep', value: stats.pending_requests, color: 'text-amber-400' },
    { icon: Wallet, label: 'Toplam Yüklenen (TL)', value: `${stats.total_revenue_tl.toFixed(2)}`, color: 'text-emerald-400' },
    { icon: TrendingUp, label: 'Toplam Harcanan (TL)', value: `${stats.total_spent_tl.toFixed(2)}`, color: 'text-rose-400' },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats">
      {cards.map(c => (
        <div key={c.label} className="aurora-card rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</span>
            <c.icon className={`w-5 h-5 ${c.color}`} />
          </div>
          <p className="text-3xl font-bold tabular-nums">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function RequestsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [actionFor, setActionFor] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [note, setNote] = useState('');
  const [viewImg, setViewImg] = useState(null);

  const load = useCallback(() => {
    adminAPI.listRequests(filter || undefined).then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    try {
      if (actionType === 'approve') await adminAPI.approveRequest(actionFor.request_id, note);
      else await adminAPI.rejectRequest(actionFor.request_id, note);
      toast.success(actionType === 'approve' ? 'Onaylandı, bakiye yüklendi' : 'Talep reddedildi');
      setActionFor(null); setNote(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewDekont = async (url) => {
    // URL is /api/uploads/<file>. Fetch with credentials and open blob
    const backend = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await fetch(`${backend}${url}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Dekont yüklenemedi');
      const blob = await res.blob();
      setViewImg(URL.createObjectURL(blob));
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {['pending','approved','rejected',''].map(s => (
          <button key={s||'all'} onClick={()=>setFilter(s)} className={`px-3 py-1.5 rounded-lg text-xs border ${filter===s ? 'bg-fuchsia-500/20 border-fuchsia-500/30 text-fuchsia-300' : 'bg-white/5 border-white/10 text-muted-foreground'}`} data-testid={`filter-${s||'all'}`}>
            {s==='pending'?'Bekleyen':s==='approved'?'Onaylı':s==='rejected'?'Reddedilen':'Tümü'}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="aurora-card rounded-3xl p-8 text-center text-muted-foreground">Talep bulunmuyor.</div>
      ) : (
        <div className="space-y-2">
          {items.map(r => (
            <div key={r.request_id} className="aurora-card rounded-2xl p-4" data-testid={`admin-req-${r.request_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-semibold tabular-nums text-lg">{r.amount.toFixed(2)} TL</p>
                  <p className="text-sm text-muted-foreground">{r.user_name} · {r.user_email}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString('tr-TR')}</p>
                  {r.note && <p className="text-xs text-fuchsia-300 mt-1">Kullanıcı notu: {r.note}</p>}
                  {r.admin_note && <p className="text-xs text-amber-300 mt-1">Admin notu: {r.admin_note}</p>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {r.dekont_url && (
                    <Button size="sm" variant="outline" onClick={()=>viewDekont(r.dekont_url)} data-testid={`view-dekont-${r.request_id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Dekont
                    </Button>
                  )}
                  {r.status === 'pending' && (
                    <>
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={()=>{setActionFor(r); setActionType('approve');}} data-testid={`approve-${r.request_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Onayla
                      </Button>
                      <Button size="sm" variant="destructive" onClick={()=>{setActionFor(r); setActionType('reject');}} data-testid={`reject-${r.request_id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reddet
                      </Button>
                    </>
                  )}
                  <StatusBadge status={r.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!actionFor} onOpenChange={(o)=>{ if(!o){setActionFor(null); setNote('');} }}>
        <DialogContent className="bg-background border-white/10">
          <DialogHeader>
            <DialogTitle>{actionType === 'approve' ? 'Talebi Onayla' : 'Talebi Reddet'}</DialogTitle>
          </DialogHeader>
          {actionFor && (
            <div className="space-y-2 text-sm">
              <p>Tutar: <span className="font-semibold">{actionFor.amount.toFixed(2)} TL</span></p>
              <p>Kullanıcı: {actionFor.user_email}</p>
              <Textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Not (opsiyonel)" className="bg-white/5 border-white/10 mt-2" data-testid="admin-action-note" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>{setActionFor(null);setNote('');}}>Vazgeç</Button>
            <Button onClick={doAction} className={actionType==='approve'?'bg-emerald-500 hover:bg-emerald-600':'bg-rose-500 hover:bg-rose-600'} data-testid="admin-action-confirm">
              {actionType==='approve'?'Onayla ve Bakiye Yükle':'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewImg} onOpenChange={(o)=>{ if(!o){ if(viewImg) URL.revokeObjectURL(viewImg); setViewImg(null);} }}>
        <DialogContent className="bg-background border-white/10 max-w-3xl">
          <DialogHeader><DialogTitle>Dekont</DialogTitle></DialogHeader>
          {viewImg && <img src={viewImg} alt="Dekont" className="w-full max-h-[70vh] object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [addFor, setAddFor] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    adminAPI.listUsers().then(r => setUsers(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    if (!q) return true;
    const ql = q.toLowerCase();
    return (u.email || '').toLowerCase().includes(ql) || (u.name || '').toLowerCase().includes(ql);
  });

  const doAdd = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Geçerli tutar girin'); return; }
    try {
      await adminAPI.addBalance(addFor.user_id, amt, note);
      toast.success(`${addFor.name} kullanıcısına ${amt.toFixed(2)} TL yüklendi`);
      setAddFor(null); setAmount(''); setNote(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleUserAction = async (u, action) => {
    const labels = { freeze: 'Dondurma', unfreeze: 'Aktifleştirme', delete: 'Silme' };
    if (action === 'delete' && !window.confirm(`${u.email} hesabını kalıcı olarak silmek istediğinize emin misiniz?`)) return;
    try {
      if (action === 'freeze') await adminAPI.freezeUser(u.user_id);
      else if (action === 'unfreeze') await adminAPI.unfreezeUser(u.user_id);
      else if (action === 'delete') await adminAPI.deleteUser(u.user_id);
      toast.success(`${labels[action]} işlemi tamamlandı`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="E-posta veya isim ara..." className="bg-white/5 border-white/10 mb-4" data-testid="admin-users-search" />

      <div className="space-y-2">
        {filtered.map(u => (
          <div key={u.user_id} className="aurora-card rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-user-${u.user_id}`}>
            <div>
              <p className="font-medium flex items-center gap-2">
                {u.name}
                {u.role === 'admin' && <span className="text-xs px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300">admin</span>}
                {u.is_active === false && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">donduruldu</span>}
              </p>
              <p className="text-xs text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
              <p className="text-xs text-muted-foreground">Kayıt: {new Date(u.created_at).toLocaleDateString('tr-TR')}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Bakiye</p>
                <p className="font-bold text-emerald-400 tabular-nums">{(u.balance_tl || 0).toFixed(2)} TL</p>
              </div>
              <Button size="sm" variant="outline" onClick={()=>setAddFor(u)} data-testid={`add-balance-${u.user_id}`}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Bakiye
              </Button>
              {u.role !== 'admin' && (
                <>
                  {u.is_active === false ? (
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300" onClick={()=>handleUserAction(u, 'unfreeze')} data-testid={`unfreeze-${u.user_id}`}>
                      Aktifleştir
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300" onClick={()=>handleUserAction(u, 'freeze')} data-testid={`freeze-${u.user_id}`}>
                      Dondur
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300" onClick={()=>handleUserAction(u, 'delete')} data-testid={`delete-user-${u.user_id}`}>
                    Sil
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!addFor} onOpenChange={(o)=>{ if(!o){setAddFor(null); setAmount(''); setNote('');} }}>
        <DialogContent className="bg-background border-white/10">
          <DialogHeader><DialogTitle>Manuel Bakiye Yükle</DialogTitle></DialogHeader>
          {addFor && (
            <div className="space-y-3 text-sm">
              <p>Kullanıcı: <span className="font-semibold">{addFor.email}</span></p>
              <p>Mevcut bakiye: <span className="font-semibold text-emerald-400">{(addFor.balance_tl || 0).toFixed(2)} TL</span></p>
              <div>
                <label className="text-xs text-muted-foreground">Yüklenecek tutar (TL)</label>
                <Input type="number" min="0" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="admin-add-amount" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Not</label>
                <Textarea value={note} onChange={(e)=>setNote(e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="admin-add-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAddFor(null)}>Vazgeç</Button>
            <Button onClick={doAdd} className="bg-emerald-500 hover:bg-emerald-600" data-testid="admin-add-confirm">Yükle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PricingTab() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    adminAPI.listPricing().then(r => {
      setItems(r.data);
      const d = {}; r.data.forEach(it => { d[it.provider] = String(it.price_tl); });
      setDrafts(d);
    }).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (provider) => {
    const v = parseFloat(drafts[provider]);
    if (isNaN(v) || v < 0) { toast.error('Geçerli bir fiyat girin'); return; }
    setSavingKey(provider);
    try {
      await adminAPI.updatePricing(provider, v);
      toast.success(`${provider} fiyatı ${v.toFixed(2)} TL olarak güncellendi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };

  return (
    <div data-testid="admin-pricing">
      <p className="text-xs text-muted-foreground mb-4">Sağlayıcı bazında indirme fiyatlarını anlık güncelle. Değişiklikler hemen aktif olur.</p>
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.provider} className="aurora-card rounded-2xl p-4 flex items-center gap-3" data-testid={`pricing-row-${it.provider}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium capitalize">{it.provider}</p>
              <p className="text-xs text-muted-foreground">Son güncelleme: {it.updated_at ? new Date(it.updated_at).toLocaleString('tr-TR') : '-'}</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.5" min="0"
                value={drafts[it.provider] ?? ''}
                onChange={(e)=>setDrafts(d => ({...d, [it.provider]: e.target.value}))}
                className="w-28 bg-white/5 border-white/10 text-right"
                data-testid={`pricing-input-${it.provider}`}
              />
              <span className="text-sm text-muted-foreground">TL</span>
              <Button size="sm" onClick={()=>save(it.provider)} disabled={savingKey===it.provider} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`pricing-save-${it.provider}`}>
                Kaydet
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    adminAPI.getSettings().then(r => setS(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      await adminAPI.updateSettings({
        image_resize_paid: !!s.image_resize_paid,
        image_resize_price: Number(s.image_resize_price) || 0,
        image_resize_dpi: Number(s.image_resize_dpi) || 96,
        max_login_attempts: Number(s.max_login_attempts) || 5,
        lockout_minutes: Number(s.lockout_minutes) || 15,
      });
      toast.success('Ayarlar güncellendi');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-settings">
      <div className="aurora-card rounded-2xl p-4">
        <h3 className="font-semibold mb-1">Görsel Ölçeklendirme</h3>
        <p className="text-xs text-muted-foreground mb-4">Aracı ücretli ya da ücretsiz olarak sunabilirsiniz.</p>

        <label className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer mb-3">
          <div>
            <p className="text-sm font-medium">Ücretli mod</p>
            <p className="text-xs text-muted-foreground">Açıkken kullanıcılardan bakiye düşülür</p>
          </div>
          <input type="checkbox" checked={!!s.image_resize_paid} onChange={(e)=>update('image_resize_paid', e.target.checked)} data-testid="settings-resize-paid" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">İşlem ücreti (TL)</label>
            <Input type="number" min="0" step="0.5" value={s.image_resize_price} onChange={(e)=>update('image_resize_price', e.target.value)} className="bg-white/5 border-white/10 mt-1" disabled={!s.image_resize_paid} data-testid="settings-resize-price" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">cm → px DPI</label>
            <Input type="number" min="10" max="1200" value={s.image_resize_dpi} onChange={(e)=>update('image_resize_dpi', e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="settings-resize-dpi" />
          </div>
        </div>
      </div>

      <div className="aurora-card rounded-2xl p-4">
        <h3 className="font-semibold mb-1">Güvenlik (Brute-force)</h3>
        <p className="text-xs text-muted-foreground mb-4">Başarısız giriş denemesi sınırı.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Maks. başarısız deneme</label>
            <Input type="number" min="1" max="50" value={s.max_login_attempts} onChange={(e)=>update('max_login_attempts', e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="settings-max-attempts" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Kilit süresi (dk)</label>
            <Input type="number" min="1" max="1440" value={s.lockout_minutes} onChange={(e)=>update('lockout_minutes', e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="settings-lockout-min" />
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-fuchsia-500 to-indigo-500" data-testid="settings-save-btn">
          {saving ? 'Kaydediliyor...' : 'Tüm Ayarları Kaydet'}
        </Button>
      </div>
    </div>
  );
}

function DownloadsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    adminAPI.listDownloads().then(r=>setItems(r.data)).catch(e=>toast.error(formatApiError(e)));
  }, []);

  const handleDownload = (downloadId) => {
    const url = adminAPI.downloadFile(downloadId);
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-2" data-testid="admin-downloads-list">
      {items.length === 0 ? (
        <div className="aurora-card rounded-3xl p-8 text-center text-muted-foreground">Indirme kaydi yok.</div>
      ) : items.map(i => (
        <div key={i.download_id} className="aurora-card rounded-2xl p-4 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{i.item_name || i.source_link}</p>
            <p className="text-xs text-muted-foreground">{i.user_email} &middot; <span className="capitalize">{i.provider}</span> &middot; {new Date(i.downloaded_at).toLocaleString('tr-TR')}</p>
          </div>
          <span className="text-sm font-semibold text-emerald-400 tabular-nums">-{i.cost_tl} TL</span>
          <Button size="sm" variant="outline" onClick={() => handleDownload(i.download_id)} className="border-fuchsia-500/40 text-fuchsia-300">
            <Download className="w-3.5 h-3.5 mr-1" /> Indir
          </Button>
        </div>
      ))}
    </div>
  );
}

function ContactTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.listContactMessages();
      setMessages(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await adminAPI.markContactRead(id);
      setMessages(prev => prev.map(m => m.message_id === id ? { ...m, read: true } : m));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Bu mesaji silmek istediginize emin misiniz?')) return;
    try {
      await adminAPI.deleteContactMessage(id);
      setMessages(prev => prev.filter(m => m.message_id !== id));
      toast.success('Mesaj silindi');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-muted-foreground">Yukleniyor...</div>;

  const unread = messages.filter(m => !m.read).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Mail className="w-5 h-5 text-fuchsia-400" />
        <h2 className="text-lg font-semibold">Iletisim Mesajlari</h2>
        {unread > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">{unread} okunmamis</span>}
      </div>

      {messages.length === 0 ? (
        <div className="aurora-card rounded-3xl p-8 text-center text-muted-foreground text-sm">Henuz iletisim mesaji yok.</div>
      ) : (
        <div className="space-y-3">
          {messages.map(m => (
            <div key={m.message_id} className={`aurora-card rounded-2xl p-5 ${!m.read ? 'border-l-4 border-l-fuchsia-500' : ''}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">{m.name}</span>
                    <span className="text-xs text-muted-foreground">&lt;{m.email}&gt;</span>
                    {!m.read && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-fuchsia-500/20 text-fuchsia-300">Yeni</span>}
                  </div>
                  {m.subject && <p className="text-sm font-medium text-fuchsia-300 mb-1">{m.subject}</p>}
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{m.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">{new Date(m.created_at).toLocaleString('tr-TR')}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {!m.read && (
                    <Button size="sm" variant="outline" onClick={() => markRead(m.message_id)} className="border-emerald-500/30 text-emerald-400">
                      <Eye className="w-3.5 h-3.5 mr-1" /> Okundu
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => deleteMsg(m.message_id)} className="border-rose-500/30 text-rose-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:  { label: 'Bekliyor',  cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    approved: { label: 'Onaylandı', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    rejected: { label: 'Reddedildi',cls: 'bg-rose-500/15 text-rose-400 border-rose-500/30' },
  };
  const v = map[status] || map.pending;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${v.cls}`}>{v.label}</span>;
}
